import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import { useNotification } from '../components/Notification';
import Modal from '../components/Modal';
import api from '../utils/api';
import { formatUsdAmount } from '../utils/money';
import { EM_DASH } from '../utils/textGlyphs';
import { LoadingLabel } from '../components/UiIcons';
import { linkWalletWithProof, isWalletLimitError } from '../utils/walletAuth';
import {
  getWalletBalance,
  getUsdcBalance,
  transferUsdc,
  depositTradingVault,
  withdrawTradingVaultWithAuth,
} from '../utils/blockchain';
import { ensureGasOrDrip, isGasDripBlocked } from '../utils/gasDrip';
import {
  peekTradingVault,
  fetchTradingVaultFromApi,
  invalidateTradingVault,
} from '../utils/tradingVaultCache';
import { peekWalletBalances, putWalletBalances } from '../utils/walletBalanceCache';
import { ethers } from 'ethers';
import { useAuth } from '../context/AuthContext';
import { syncChainConfigFromServer } from '../utils/syncChainConfig';

const ITEMS_PER_PAGE = 20;

function BalanceSkeleton({ wide = false }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      <div
        className={`h-8 rounded-md bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse ${
          wide ? 'w-40' : 'w-32'
        }`}
      />
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
        Fetching on-chain…
      </div>
    </div>
  );
}

export default function WalletPage() {
  const { account, connect, ensureConnected, isBaseSepolia } = useWallet();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [ethBalance, setEthBalance] = useState(null);
  const [usdcBalance, setUsdcBalance] = useState(null);
  const [balancesLoading, setBalancesLoading] = useState(false);

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [token, setToken] = useState('USDC'); // USDC | ETH
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);

  const [txRows, setTxRows] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txTotalPages, setTxTotalPages] = useState(1);

  const [vaultInfo, setVaultInfo] = useState(null);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultDepositAmt, setVaultDepositAmt] = useState('');
  const [vaultWithdrawAmt, setVaultWithdrawAmt] = useState('');
  const [vaultBusy, setVaultBusy] = useState(false);

  const txFetchGenRef = useRef(0);
  const vault403NotifiedRef = useRef(false);
  const walletDataKeyRef = useRef('');

  const shortAccount = useMemo(() => {
    if (!account) return '';
    return `${account.slice(0, 6)}...${account.slice(-4)}`;
  }, [account]);

  const refreshBalances = async ({ force = false } = {}) => {
    if (!account) return;
    const cached = peekWalletBalances(account);
    if (cached?.eth != null) setEthBalance(cached.eth);
    if (cached?.usdc != null) setUsdcBalance(cached.usdc);
    setBalancesLoading(!(cached?.eth != null || cached?.usdc != null));
    try {
      const [eth, usdc] = await Promise.all([
        getWalletBalance(account, { force }),
        getUsdcBalance(account),
      ]);
      if (eth != null) setEthBalance(eth);
      if (usdc != null) setUsdcBalance(usdc);
      putWalletBalances(account, { eth, usdc });
    } catch (e) {
      console.error('refreshBalances:', e);
    } finally {
      setBalancesLoading(false);
    }
  };

  const ensureWalletLinked = async () => {
    if (!user || !account) return false;
    try {
      const { data } = await api.get('/auth/me');
      const wallets = Array.isArray(data?.user?.wallets) ? data.user.wallets : [];
      const linked = wallets.some((w) => String(w).toLowerCase() === String(account).toLowerCase());
      if (linked) return true;
      await linkWalletWithProof(account);
      return true;
    } catch (e) {
      if (!isWalletLimitError(e)) {
        showNotification(e.response?.data?.message || 'Link this wallet to your account first', 'warning');
      }
      return false;
    }
  };

  const refreshVault = async ({ quiet = false, force = false } = {}) => {
    if (!account) {
      setVaultInfo(null);
      setVaultLoading(false);
      return;
    }
    const cached = peekTradingVault(account);
    if (cached) setVaultInfo(cached);
    else setVaultLoading(true);
    try {
      const data = await fetchTradingVaultFromApi(account, { timeout: 10000, force });
      setVaultInfo(data);
      vault403NotifiedRef.current = false;
    } catch (e) {
      if (e?.response?.status === 403 && user?._id) {
        const linked = await ensureWalletLinked();
        if (linked) {
          try {
            const data = await fetchTradingVaultFromApi(account, { timeout: 10000, force: true });
            setVaultInfo(data);
            vault403NotifiedRef.current = false;
            return;
          } catch {
            /* fall through */
          }
        }
        if (!cached) setVaultInfo(null);
        if (!quiet && !vault403NotifiedRef.current) {
          vault403NotifiedRef.current = true;
          showNotification(
            e.response?.data?.message || 'Link this wallet to your account to view the trading vault.',
            'warning'
          );
        }
        return;
      }
      if (!cached) setVaultInfo(null);
      if (e?.response?.status === 403 && !quiet && !vault403NotifiedRef.current) {
        vault403NotifiedRef.current = true;
        showNotification(
          e.response?.data?.message || 'Link this wallet to your account to view the trading vault.',
          'warning'
        );
      }
    } finally {
      setVaultLoading(false);
    }
  };

  const fetchTransactions = useCallback(async (page = 1) => {
    if (!account) return;
    const gen = ++txFetchGenRef.current;
    setTxLoading(true);
    try {
      const { data } = await api.get('/transactions/me', {
        params: { page, limit: ITEMS_PER_PAGE },
      });
      if (gen !== txFetchGenRef.current) return;
      setTxRows(data?.rows || []);
      setTxPage(data?.page || page);
      setTxTotalPages(data?.totalPages || 1);
    } catch {
      if (gen !== txFetchGenRef.current) return;
      setTxRows([]);
      setTxTotalPages(1);
    } finally {
      if (gen === txFetchGenRef.current) setTxLoading(false);
    }
  }, [account]);

  useEffect(() => {
    syncChainConfigFromServer().catch(() => {});
  }, []);

  useEffect(() => {
    const key = `${account || ''}:${user?._id || ''}`;
    if (!account) {
      walletDataKeyRef.current = '';
      setTxRows([]);
      setVaultInfo(null);
      setEthBalance(null);
      setUsdcBalance(null);
      return;
    }
    if (walletDataKeyRef.current === key) return;
    walletDataKeyRef.current = key;
    const cachedVault = peekTradingVault(account);
    if (cachedVault) setVaultInfo(cachedVault);
    const cachedBal = peekWalletBalances(account);
    if (cachedBal?.eth != null) setEthBalance(cachedBal.eth);
    if (cachedBal?.usdc != null) setUsdcBalance(cachedBal.usdc);
    refreshBalances();
    refreshVault({ quiet: true });
    fetchTransactions(1);
  }, [account, user?._id, fetchTransactions]);

  const handleVaultDeposit = async () => {
    if (!account) {
      showNotification('Please connect your wallet first', 'warning');
      return;
    }
    const amt = parseFloat(String(vaultDepositAmt || '').trim());
    if (!Number.isFinite(amt) || amt <= 0) {
      showNotification('Enter a valid deposit amount', 'warning');
      return;
    }
    setVaultBusy(true);
    try {
      await ensureConnected();
      if (!isBaseSepolia) {
        showNotification('Please switch your wallet to Base Sepolia', 'warning');
        return;
      }
      await syncChainConfigFromServer();
      if (!(await ensureWalletLinked())) return;
      const vaultDrip = { label: 'vault deposit', playType: 'market', showNotification };
      const okGas = await ensureGasOrDrip(account, vaultDrip);
      if (!okGas) return;
      await depositTradingVault(amt, { gasDrip: vaultDrip });
      invalidateTradingVault(account);
      showNotification('Deposited to trading vault', 'success');
      setVaultDepositAmt('');
      await Promise.all([refreshBalances({ force: true }), refreshVault({ force: true })]);
    } catch (e) {
      if (isGasDripBlocked(e)) return;
      console.error('handleVaultDeposit:', e);
      showNotification(e?.shortMessage || e?.message || 'Deposit failed', 'error');
    } finally {
      setVaultBusy(false);
    }
  };

  const handleVaultWithdraw = async () => {
    if (!account) {
      showNotification('Please connect your wallet first', 'warning');
      return;
    }
    const avail = Number(vaultInfo?.availableUsdc ?? 0);
    const amt = parseFloat(String(vaultWithdrawAmt || '').trim());
    if (!Number.isFinite(amt) || amt <= 0) {
      showNotification('Enter a valid withdraw amount', 'warning');
      return;
    }
    if (!Number.isFinite(avail) || avail <= 0) {
      showNotification('Nothing withdrawable', 'warning');
      return;
    }
    if (amt > avail + 1e-9) {
      showNotification(
        `Amount exceeds withdrawable balance (${formatUsdAmount(avail)})`,
        'warning'
      );
      return;
    }
    setVaultBusy(true);
    try {
      await ensureConnected();
      if (!isBaseSepolia) {
        showNotification('Please switch your wallet to Base Sepolia', 'warning');
        return;
      }
      const vaultDrip = { label: 'vault withdraw', playType: 'market', showNotification };
      const okGas = await ensureGasOrDrip(account, vaultDrip);
      if (!okGas) return;
      const { data } = await api.post('/orderbook/vault/withdraw-auth', {
        walletAddress: account,
        amountUsdc: amt,
      });
      await withdrawTradingVaultWithAuth(data.amountWei, data.nonce, data.deadline, data.signature, {
        gasDrip: vaultDrip,
      });
      invalidateTradingVault(account);
      showNotification(`Withdrew ${formatUsdAmount(amt)} from trading vault`, 'success');
      setVaultWithdrawAmt('');
      await Promise.all([refreshBalances({ force: true }), refreshVault({ force: true })]);
    } catch (e) {
      if (isGasDripBlocked(e)) return;
      console.error('handleVaultWithdraw:', e);
      showNotification(e?.response?.data?.message || e?.message || 'Withdraw failed', 'error');
    } finally {
      setVaultBusy(false);
    }
  };

  const vaultAmountLabel = (value) => {
    if (vaultInfo == null && vaultLoading) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400">
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
          Fetching…
        </span>
      );
    }
    return formatUsdAmount(value ?? 0);
  };

  const setWithdrawMax = () => {
    const avail = Number(vaultInfo?.availableUsdc ?? 0);
    if (!Number.isFinite(avail) || avail <= 0) {
      setVaultWithdrawAmt('');
      return;
    }
    // Trim trailing zeros for a clean input (up to 6 decimals to match USDC).
    setVaultWithdrawAmt(String(parseFloat(avail.toFixed(6))));
  };

  const handleSend = async () => {
    if (!account) {
      showNotification('Please connect your wallet first', 'warning');
      return;
    }
    const toAddr = String(to || '').trim();
    const amtStr = String(amount || '').trim();
    if (!toAddr || !ethers.isAddress(toAddr)) {
      showNotification('Enter a valid recipient address', 'warning');
      return;
    }
    const amt = parseFloat(amtStr);
    if (!Number.isFinite(amt) || amt <= 0) {
      showNotification('Enter a valid amount', 'warning');
      return;
    }

    setSending(true);
    try {
      await ensureConnected();
      if (!isBaseSepolia) {
        showNotification('Please switch your wallet to Base Sepolia', 'warning');
        return;
      }

      let txHash = '';
      if (token === 'ETH') {
        if (typeof window.ethereum === 'undefined') {
          showNotification('No wallet provider found', 'error');
          return;
        }
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const tx = await signer.sendTransaction({
          to: toAddr,
          value: ethers.parseEther(amtStr),
        });
        txHash = tx.hash;
        await tx.wait();
      } else {
        const transferDrip = { label: 'USDC transfer', playType: 'market', showNotification };
        const okGas = await ensureGasOrDrip(account, transferDrip);
        if (!okGas) return;
        txHash = await transferUsdc(toAddr, amtStr, { gasDrip: transferDrip });
      }

      // Log to backend for Wallet transaction history table
      try {
        await api.post('/transactions', {
          action: token === 'ETH' ? 'wallet_transfer_eth' : 'wallet_transfer_usdc',
          txHash,
          amount: amt,
          currency: token,
          itemType: 'none',
          meta: { to: toAddr },
        });
      } catch {
        // ignore logging errors
      }

      showNotification(`Transfer confirmed! TX: ${String(txHash).slice(0, 10)}...`, 'success');
      setTransferOpen(false);
      setTo('');
      setAmount('');
      await refreshBalances({ force: true });
      await fetchTransactions(1);
    } catch (e) {
      if (isGasDripBlocked(e)) return;
      console.error('handleSend:', e);
      showNotification(e?.shortMessage || e?.message || 'Transfer failed', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Wallet</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              View balances, receive, transfer, and your platform transaction history.
            </p>
          </div>
          {!account ? (
            <button
              type="button"
              onClick={connect}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Connect Wallet
            </button>
          ) : (
            <div className="text-sm text-gray-700 dark:text-gray-300 font-mono">
              {shortAccount}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-blue-200/70 dark:border-blue-800/50 bg-blue-50/80 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-900 dark:text-blue-100 leading-relaxed">
          <strong className="font-semibold">Main wallet</strong> is your on-chain account (USDC + ETH for gas).{' '}
          <strong className="font-semibold">WRG trading vault</strong> is a separate balance used only for market
          orderbook trades — deposit USDC into the vault before buying shares; withdrawals return USDC to your main
          wallet.
        </div>

        {account && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setReceiveOpen(true)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Deposit / Receive
              </button>
              <button
                type="button"
                onClick={() => setTransferOpen(true)}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Transfer
              </button>
              <button
                type="button"
                onClick={() => refreshBalances({ force: true })}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Refresh balances
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Main wallet · Base ETH (gas)
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white min-h-[2.25rem]">
                  {balancesLoading && ethBalance == null ? (
                    <BalanceSkeleton />
                  ) : ethBalance == null ? (
                    <span className="text-base font-semibold text-gray-500 dark:text-gray-400">Unavailable</span>
                  ) : (
                    `${Number(ethBalance).toFixed(6)} ETH`
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Pays network fees for boosts, claims, and transfers.
                </p>
              </div>
              <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Main wallet · USDC
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white min-h-[2.25rem]">
                  {balancesLoading && usdcBalance == null ? (
                    <BalanceSkeleton wide />
                  ) : usdcBalance == null ? (
                    <span className="text-base font-semibold text-gray-500 dark:text-gray-400">Unavailable</span>
                  ) : (
                    formatUsdAmount(usdcBalance)
                  )}
                </div>
                {usdcBalance != null && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {Number(usdcBalance).toFixed(2)} USDC
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Use for boosts and vault deposits. Not spent directly on market fills.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Trading Vault */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4 gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">WRG trading vault</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Market orderbook balance — deposit from your main wallet USDC, then trade on Market tabs. Fills debit
                this vault; winning claims pay USDC back to your main wallet.
              </p>
            </div>
            <button
              type="button"
              onClick={() => refreshVault({ force: true })}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              disabled={!account}
            >
              Refresh
            </button>
          </div>

          {!account ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">Connect your wallet to manage your trading vault.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Vault (on-chain)</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {vaultAmountLabel(vaultInfo?.onChainVaultUsdc)}
                  </div>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Reserved</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {vaultAmountLabel(vaultInfo?.reservedUsdc)}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                    Open buys {vaultAmountLabel(vaultInfo?.openBuyOrdersReservedUsdc)}
                    {' · '}
                    Settling {vaultAmountLabel(vaultInfo?.pendingSettlementUsdc)}
                  </div>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Withdrawable</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {vaultAmountLabel(vaultInfo?.availableUsdc)}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                USDC in open positions is <strong>not withdrawable</strong>. While a fill is settling it appears under
                Reserved (Settling). After settlement it is removed from the on-chain vault balance, so Withdrawable
                only shows free USDC. Reserved also locks open/partial buy orders that have not filled yet.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Deposit USDC
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={vaultDepositAmt}
                    onChange={(e) => setVaultDepositAmt(e.target.value)}
                    placeholder="e.g. 25"
                    className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Deposits stay in your vault until you withdraw, or they are used to settle filled orders.
                  </p>
                  <button
                    type="button"
                    onClick={handleVaultDeposit}
                    disabled={vaultBusy}
                    className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {vaultBusy ? <LoadingLabel text="Processing" /> : 'Deposit to vault'}
                  </button>
                </div>

                <div className="space-y-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Withdraw USDC
                    </label>
                    <button
                      type="button"
                      onClick={setWithdrawMax}
                      disabled={vaultBusy || !(Number(vaultInfo?.availableUsdc) > 0)}
                      className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-40"
                    >
                      Max
                    </button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={vaultWithdrawAmt}
                    onChange={(e) => setVaultWithdrawAmt(e.target.value)}
                    placeholder="e.g. 10"
                    className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Withdrawable:{' '}
                    <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                      {vaultAmountLabel(vaultInfo?.availableUsdc)}
                    </span>
                    . Use Max to withdraw everything available.
                  </p>
                  <button
                    type="button"
                    onClick={handleVaultWithdraw}
                    disabled={vaultBusy}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                  >
                    {vaultBusy ? <LoadingLabel text="Processing" /> : 'Withdraw from vault'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Transactions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Transaction history</h2>
            <button
              type="button"
              onClick={() => fetchTransactions(txPage)}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              disabled={!account}
            >
              Refresh
            </button>
          </div>

          {!account ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Connect your wallet to see your transaction history.
            </div>
          ) : txLoading ? (
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">
              <LoadingLabel text="Loading" className="justify-center" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">TX</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {txRows.map((row) => (
                      <tr key={row._id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {row.action}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {row.amount != null
                            ? row.currency === 'ETH'
                              ? `${Number(row.amount).toFixed(6)} ETH`
                              : formatUsdAmount(row.amount)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">
                          {row.txHash ? (
                            <a
                              href={`https://sepolia.basescan.org/tx/${String(row.txHash)}`}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="hover:underline"
                              title={String(row.txHash)}
                            >
                              {`${String(row.txHash).slice(0, 10)}...`}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {txRows.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No transactions yet.
                  </div>
                )}
              </div>

              {txTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      const next = Math.max(1, txPage - 1);
                      fetchTransactions(next);
                    }}
                    disabled={txPage <= 1}
                    className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Page {txPage} of {txTotalPages}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = Math.min(txTotalPages, txPage + 1);
                      fetchTransactions(next);
                    }}
                    disabled={txPage >= txTotalPages}
                    className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Receive modal */}
      {receiveOpen && (
        <Modal isOpen={true} onClose={() => setReceiveOpen(false)} title="Deposit / Receive">
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Send <strong>Base ETH</strong> (for gas) or <strong>USDC</strong> to this address:
            </p>
            <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-700 font-mono text-sm break-all text-gray-900 dark:text-white">
              {account || '—'}
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(account || '');
                  showNotification('Address copied', 'success');
                } catch {
                  showNotification('Could not copy address', 'warning');
                }
              }}
              disabled={!account}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              Copy address
            </button>
          </div>
        </Modal>
      )}

      {/* Transfer modal */}
      {transferOpen && (
        <Modal isOpen={true} onClose={() => setTransferOpen(false)} title="Transfer">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Token</label>
              <select
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="USDC">USDC</option>
                <option value="ETH">ETH</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
              <input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="0x..."
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={token === 'ETH' ? '0.001' : '10'}
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
              {token === 'USDC' ? (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formatUsdAmount(amount || 0)}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {sending ? <LoadingLabel text="Sending" /> : 'Send'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

